import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  Text,
  useOutsideClick,
} from '@chakra-ui/react';

export interface ISearchableOption {
  value: string;
  label: string;
}

interface IProps {
  options: ISearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  borderColor?: string;
  isDisabled?: boolean;
  // Mensagem quando não há resultados na busca.
  emptyMessage?: string;
}

/**
 * Campo de busca + seleção (autocomplete). O usuário digita para filtrar e
 * escolhe um item da lista — nunca digita o valor final, garantindo dados
 * padronizados. Grava a `value` da opção selecionada via onChange.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Pesquisar...',
  size = 'sm',
  borderColor,
  isDisabled = false,
  emptyMessage = 'Nenhum resultado',
}: IProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  // Rótulo da opção atualmente selecionada (para exibir quando fechado).
  const selectedLabel = useMemo(() => {
    const found = options.find(o => o.value === value);
    return found ? found.label : '';
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = (option: ISearchableOption) => {
    onChange(option.value);
    setQuery('');
    setIsOpen(false);
  };

  return (
    <Box position="relative" ref={containerRef}>
      <Input
        size={size}
        isDisabled={isDisabled}
        placeholder={placeholder}
        color="#1a202c"
        borderColor={borderColor}
        // Enquanto aberto mostramos o texto digitado; fechado, o rótulo salvo.
        value={isOpen ? query : selectedLabel}
        onChange={e => {
          setQuery(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        autoComplete="off"
      />

      {isOpen && (
        <List
          position="absolute"
          zIndex={20}
          mt={1}
          w="100%"
          maxH="220px"
          overflowY="auto"
          bg="white"
          color="#1a202c"
          borderRadius="md"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.200"
        >
          {filtered.length === 0 ? (
            <ListItem px={3} py={2}>
              <Text fontSize="sm" color="gray.500">
                {emptyMessage}
              </Text>
            </ListItem>
          ) : (
            filtered.map(option => (
              <ListItem
                key={option.value}
                px={3}
                py={2}
                cursor="pointer"
                fontSize="sm"
                bg={option.value === value ? 'blue.50' : undefined}
                _hover={{ bg: 'blue.100' }}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </ListItem>
            ))
          )}
        </List>
      )}
    </Box>
  );
}
